import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import BottomDrawer from '../Drawer/Drawer';
import { mediaBelow, pxToRem } from '../../assets/styles/helper';
import { variables } from '../../assets/styles/variables';
import themeColors from '../../assets/styles/themeColors';
import SectionLoader from '../Loader/SectionLoader';
import SelectItem from './components/SelectItem';
import paymentTypes, {
  PAYMENT_METHOD_TYPES,
  PAYMENT_METHOD_TYPES_ICONS,
} from '../../util/enum/api/paymentTypes';
import { formatMoneyNumeral } from '../../util/helpers/numeralHelpers';
import AddPaymentMethod from './AddPaymentMethod';
import { fetchPortfolioBalances } from '../../store/actions/portfolio/portfolioActions';

const PaymentOptionsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${pxToRem(12)};
`;

const AddPaymentMethodButton = styled.button`
  width: 100%;
  border-radius: ${variables.borderRadius.borderRadius};
  border: 1px dashed ${themeColors.colorGrey3};
  font-size: ${pxToRem(14)};
  line-height: 1.4;
  font-weight: 500;
  color: ${themeColors.colorTextPrimary};
  background-color: transparent;
  padding: ${pxToRem(10)};
  cursor: pointer;
`;

const MaskedContainer = styled.div`
  font-size: 12px;
  color: ${themeColors.colorTextSecondary};
  display: flex;
  align-self: flex-start;

  span {
    font-size: 12px;
  }
`;

const PaymentMethodLimit = styled.p`
  font-size: 14px;
  font-weight: 400;
  color: ${themeColors.colorTextSecondary};

  ${mediaBelow(variables.breakpoints.bpMd)} {
    font-size: 12px;
  }
`;

const SelectPaymentOptions = ({
  open,
  setOpen,
  paymentMethods,
  selectedPaymentMethod,
  setSelectedPaymentMethod,
  setIsAddAddressOpen,
  isAddAddressOpen,
  accountUid,
  isDeliveryOnlyAccount,
  setSuccessToast,
  wizardData,
  setWizardData,
  isAddPaymentMethodOpen,
  setIsAddPaymentMethodOpen,
  creditCardOnly,
  onAddPaymentMethodClick,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [isNewCardAdded, setIsNewCardAdded] = useState(false);
  const [isNewACHAdded, setIsNewACHAdded] = useState(false);
  // Ref so the selection intent survives the state reset and is still
  // available when paymentMethods refreshes after fetchPortfolioBalances.
  const pendingSelectionRef = useRef(null);

  useEffect(() => {
    if (!isNewCardAdded && !isNewACHAdded) return;
    if (isNewCardAdded || isNewACHAdded) {
      pendingSelectionRef.current = isNewCardAdded ? 'card' : 'ach';
      setIsNewCardAdded(false);
      setIsNewACHAdded(false);
      setIsAddPaymentMethodOpen(false);
      dispatch(fetchPortfolioBalances({ accountUid }));
      setSuccessToast({
        isOpen: true,
        title: t('buyWizard.paymentAddedTitle'),
        message: t('buyWizard.paymentAddedMessage'),
      });
      handleClose();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountUid, dispatch, isNewCardAdded, isNewACHAdded]);

  useEffect(() => {
    if (!paymentMethods?.length || !pendingSelectionRef.current) return;
    const pending = pendingSelectionRef.current;
    pendingSelectionRef.current = null;

    // Defer so this runs after OrderSelection's payment-selection effect,
    // which fires in the same render cycle and would otherwise override
    // this selection with null (no IsDefault on a freshly added card).
    setTimeout(() => {
      if (pending === 'card') {
        const filteredCreditCard = paymentMethods?.filter(
          (account) => account?.PaymentMethodType === 3,
        );
        if (filteredCreditCard.length) {
          const newCard = filteredCreditCard[filteredCreditCard.length - 1];
          setSelectedPaymentMethod(newCard);
          setWizardData((prev) => ({
            ...prev,
            selectedPaymentMethod: newCard,
          }));
        }
      }
      if (pending === 'ach') {
        const filteredBankPayment = paymentMethods?.filter(
          (account) => account?.PaymentMethodType === 2,
        );
        if (filteredBankPayment.length) {
          const newACH = filteredBankPayment[filteredBankPayment.length - 1];
          setSelectedPaymentMethod(newACH);
          setWizardData((prev) => ({ ...prev, selectedPaymentMethod: newACH }));
        }
      }
    }, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentMethods]);

  const paymentMethodInfo = (method) => {
    if (method?.AccountBalance || method?.AccountBalance === 0) {
      return (
        <MaskedContainer>
          {formatMoneyNumeral(method?.AccountBalance)}
        </MaskedContainer>
      );
    }
    if (method?.BankName) {
      return (
        <>
          <MaskedContainer>
            <span>***</span>
            <span>{method?.BankAccountMask}</span>
          </MaskedContainer>
          <PaymentMethodLimit>
            {t('buyWizard.payment.limitPerDay', {
              limit: method?.MaximumLimit,
            })}
          </PaymentMethodLimit>
        </>
      );
    }
    if (method?.CreditCardType) {
      return (
        <>
          <MaskedContainer>
            <span>***</span>
            <span>{method?.Last4Digits}</span>
          </MaskedContainer>
          {method?.MaximumLimit != null && (
            <PaymentMethodLimit>
              {t('buyWizard.payment.limitPerOrder', {
                limit: method?.MaximumLimit,
              })}
            </PaymentMethodLimit>
          )}
        </>
      );
    }
  };

  const getPaymentMethodLabel = (method) => {
    if (method?.AccountBalance || method?.AccountBalance === 0) {
      return `${t(
        `buyWizard.payment.paymentMethods.${
          paymentTypes[method?.PaymentMethodType]
        }`,
      )} ${formatMoneyNumeral(method?.AccountBalance)}`;
    }

    if (method?.BankName) {
      return `${method?.BankName ?? ''} ***${method?.BankAccountMask}`;
    }

    if (method?.CreditCardType) {
      return `${t(
        `buyWizard.payment.paymentMethods.${
          paymentTypes[method?.PaymentMethodType]
        }`,
      )} ***${method?.Last4Digits}`;
    }

    return '';
  };

  const handleClose = () => {
    setOpen(false);
  };

  if (isAddPaymentMethodOpen) {
    return (
      <AddPaymentMethod
        isOpen={isAddPaymentMethodOpen}
        onClose={() => setIsAddPaymentMethodOpen(false)}
        avoidClearingStorage
        setIsAddAddressOpen={setIsAddAddressOpen}
        isAddAddressOpen={isAddAddressOpen}
        setIsAddPaymentMethodOpen={setIsAddPaymentMethodOpen}
        setIsNewCardAdded={setIsNewCardAdded}
        setIsNewACHAdded={setIsNewACHAdded}
        setOpen={setOpen}
        creditCardOnly={creditCardOnly}
      />
    );
  }

  const handleSelectPayment = (paymentMethod) => {
    setSelectedPaymentMethod(paymentMethod);
    setWizardData({ ...wizardData, selectedPaymentMethod: paymentMethod });
    setSuccessToast({
      isOpen: true,
      title: t('buyWizard.paymentMethodCahnged'),
      message: t('buyWizard.nowUsingPaymentMethod', {
        paymentMethod: getPaymentMethodLabel(paymentMethod),
      }),
    });

    handleClose();
  };

  return (
    <BottomDrawer
      isOpen={open}
      onClose={handleClose}
      title={t('buyWizard.payment.selectPaymentOptions')}
    >
      <SectionLoader
      // isLoading={isLoading}
      >
        <PaymentOptionsWrapper>
          <AddPaymentMethodButton
            onClick={() => {
              if (typeof onAddPaymentMethodClick === 'function') {
                onAddPaymentMethodClick();
              }
              setIsAddPaymentMethodOpen(true);
            }}
          >
            {t('buyWizard.payment.addPaymentMethod')}
          </AddPaymentMethodButton>
          {paymentMethods
            ?.filter(
              (paymentMethod) =>
                !(
                  isDeliveryOnlyAccount &&
                  paymentMethod?.PaymentMethodType ===
                    PAYMENT_METHOD_TYPES.ACCOUNT_BALANCE
                ),
            )
            ?.map((paymentMethod) => (
              <SelectItem
                key={paymentMethod?.PaymentMethodUid}
                selectItemTitle={`${paymentMethod?.BankName ?? ''} ${t(
                  `buyWizard.payment.paymentMethods.${
                    paymentTypes[paymentMethod?.PaymentMethodType]
                  }`,
                )}`}
                selectItemSubtitle={paymentMethodInfo(paymentMethod)}
                icon={
                  PAYMENT_METHOD_TYPES_ICONS[paymentMethod?.PaymentMethodType]
                }
                selectItemRibbon={
                  paymentMethod?.PaymentMethodType !==
                    PAYMENT_METHOD_TYPES.CREDIT_CARD &&
                  t('buyWizard.payment.savePrecent')
                }
                backgroundColor={themeColors.colorSuccess}
                isSelected={
                  selectedPaymentMethod?.PaymentMethodUid ===
                  paymentMethod?.PaymentMethodUid
                }
                onClick={() => {
                  handleSelectPayment(paymentMethod);
                }}
                // TO DO: Change below isDisabled to use isPaymentMethodDisabled when getQuote ias added
                isDisabled={
                  paymentMethod?.PaymentMethodType ===
                    PAYMENT_METHOD_TYPES.ACH &&
                  paymentMethod?.BankAccountSetupType === 2
                }
                // isDisabled={isPaymentMethodDisabled(
                //   paymentMethod,
                //   totalAmountWithCreditCard,
                //   totalAmountWithoutCreditCard,
                //   isAccountBalanceDisabled,
                // )}
              />
            ))}
        </PaymentOptionsWrapper>
      </SectionLoader>
    </BottomDrawer>
  );
};

SelectPaymentOptions.propTypes = {
  open: PropTypes.bool,
  setOpen: PropTypes.func,
  wizardData: PropTypes.shape({}),
  paymentMethods: PropTypes.arrayOf(PropTypes.shape({})),
  selectedPaymentMethod: PropTypes.shape({
    PaymentMethodUid: PropTypes.number,
  }),
  setSelectedPaymentMethod: PropTypes.func,
  setIsAddAddressOpen: PropTypes.func,
  isAddAddressOpen: PropTypes.bool,
  accountUid: PropTypes.string,
  isDeliveryOnlyAccount: PropTypes.bool,
  setSuccessToast: PropTypes.func,
  setWizardData: PropTypes.func,
  isAddPaymentMethodOpen: PropTypes.bool,
  setIsAddPaymentMethodOpen: PropTypes.func,
  creditCardOnly: PropTypes.bool,
  onAddPaymentMethodClick: PropTypes.func,
};

export default SelectPaymentOptions;
